import { Modal, Spin, Tabs } from "antd";
import { useEffect, useRef, useState } from "react";
import apiFactory from "../../../api";
import { CHATTING } from "../../../config/Constant";
import { useAuthLayoutContext } from "../../../context/AuthLayoutContext";
import { useInfoUser } from "../../../store/UserStore";
import { updateOrgList } from "../../../utils/OrganizationHelper";
import { openNotificationError } from "../../../utils/Utils";
import { BasicMemberInfoV2 } from "../../select/directory/BasicMemberInfoV2";

const MemberList = ({ mailId, activeKey }) => {
  const { setOrgList: setOriginalOrgList } = useAuthLayoutContext();
  const { user } = useInfoUser();
  const [memberSearch, setMemberSearch] = useState({
    search: {
      mailId,
      memberType: null,
      viewType: activeKey,
    },
    ts: new Date(),
    page: 1,
    limit: CHATTING.MEMBER_LIMIT,
  });
  const [memberList, setMemberList] = useState([]);
  const lastObserver = useRef();
  const [isLoading, setIsLoading] = useState(false);
  const [isMoreMember, setIsMoreMember] = useState(false);

  const lastMemberRef = (node) => {
    if (isLoading || !isMoreMember) return;
    if (lastObserver.current) lastObserver.current.disconnect();

    lastObserver.current = new IntersectionObserver(async (entries) => {
      if (entries[0].isIntersecting) {
        setIsLoading(true);
        setMemberSearch((prevState) => {
          return {
            ...prevState,
            page: prevState.page + 1,
          };
        });
      }
    });

    if (node) lastObserver.current.observe(node);
  };

  const fetchMembersData = async () => {
    setIsLoading(true);

    try {
      const result = await apiFactory.userMailApi.getMemberPage(memberSearch);

      if (result?.status !== 200) {
        openNotificationError(result?.message);
        return;
      }

      setMemberList([...memberList, ...result?.data?.items]);
      updateOrgList(setOriginalOrgList, result?.data?.items);

      setIsMoreMember(result?.data?.items?.length === CHATTING.MEMBER_LIMIT);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMembersData();
  }, [memberSearch]);

  if (!memberList?.length && isLoading) {
    return (
      <div className="h-[60vh] flex items-center justify-center">
        <Spin />
      </div>
    );
  }

  if (!memberList?.length && !isLoading) {
    return (
      <div className="h-[60vh] flex items-center justify-center">
        <div>
          <span>No data found</span>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[60vh] overflow-y-scroll">
      {memberList?.map((member, index) => {
        if (member?.organizations?.[0]) {
          member.organizations[0].avatar = member?.avatar || null;
        }
        return (
          <div
            key={member?.userId}
            ref={index === memberList?.length - 1 ? lastMemberRef : null}
          >
            <BasicMemberInfoV2
              member={member}
              memberGroup={member}
              screenInfo={"READ_UNREAD"}
              isHost={member?.role === "HOST"}
              isAdmin={member?.role === "ADMIN"}
              isMe={member?.userId === user?.userId}
              hasRead={member?.hasRead}
              lastSeenTs={member?.lastSeenTs}
              hasOrganizations={true}
            />
          </div>
        );
      })}
    </div>
  );
};

const MailMemberModal = ({ mailId, oldConversationId, onCancel, isOpen }) => {
  const { languageMap } = useInfoUser();
  const [tabs, setTabs] = useState([]);
  const [memberList, setMemberList] = useState([]);
  const [activeKey, setActiveKey] = useState("ALL");

  const changeTab = (key) => {
    setActiveKey(key);
    setMemberList([]);
  };

  useEffect(() => {
    const items = [
      {
        key: "ALL",
        label: languageMap?.["modal.seen.all"] ?? "All",

        children: <MemberList mailId={mailId} activeKey={"ALL"} />,
      },
      {
        key: "RECIPIENT",
        label: "Recipient",
        children: <MemberList mailId={mailId} activeKey={"RECIPIENT"} />,
      },
      {
        key: "REFERENCE",
        label: "Reference",
        children: <MemberList mailId={mailId} activeKey={"REFERENCE"} />,
      },
      ...(oldConversationId
        ? []
        : [
            {
              key: "READ",
              label: languageMap?.["modal.seen.read"] ?? "Read",
              children: <MemberList mailId={mailId} activeKey={"READ"} />,
            },
            {
              key: "UNREAD",
              label: languageMap?.["modal.seen.unread"] ?? "Unread",
              children: <MemberList mailId={mailId} activeKey={"UNREAD"} />,
            },
          ]),
    ];
    setTabs(items);
  }, [memberList, languageMap, oldConversationId]);

  return (
    <Modal
      title={
        languageMap?.["modal.seen.title"] ?? "Participant message view status"
      }
      open={isOpen}
      onCancel={onCancel}
      width="800px"
      footer={false}
      centered
    >
      <Tabs
        defaultActiveKey="ALL"
        activeKey={activeKey}
        items={tabs}
        onChange={changeTab}
      />
    </Modal>
  );
};

export { MailMemberModal };
